import axios from 'axios';

import {
  Auth0TokenResponsePayload,
  UserPaymentMethod,
  UserProfile,
  UserProfileUpdateRequestPayload,
} from '../../types/auth';
import {
  AUTH0_AUDIENCE,
  AUTH0_CLIENT_ID,
  AUTH0_CLIENT_SECRET,
  AUTH0_DOMAIN,
  DEPLOYMENT_URL,
} from '../../helpers/envs';
import { API, composeDefaultHeaders } from '../index';
import { APIAddress } from '../../types/common';

/**
 * Starts a passwordless email sign in process.
 * @param email Email address used to sign in
 */
export const signInEmailStart = (email: string) =>
  axios.post('/api/passwordless-start', {
    email,
  });

/**
 * Verifies an email with a verification code received by user in a paswordless sign in process.
 * @param email Email address used to sign in
 * @param code Verification code sent to user's email address
 */
export const signInEmailVerifyCode = (email: string, code: string) =>
  axios.post<Auth0TokenResponsePayload>('/api/verify-code', {
    email,
    code,
  });

/**
 * Exchanges authentication code for access token.
 * @param code Code to exchange for access token
 */
export const signInCodeExchange = (code: string) =>
  axios.post<Auth0TokenResponsePayload>(AUTH0_DOMAIN + '/oauth/token', {
    grant_type: 'authorization_code',
    client_id: AUTH0_CLIENT_ID,
    client_secret: AUTH0_CLIENT_SECRET,
    redirect_uri: DEPLOYMENT_URL + '/callback',
    audience: AUTH0_AUDIENCE,
    code,
  });

/**
 * Updates partial user profile data.
 * @param address User's delivery address
 * @param fields Object containing user's personal data
 */
export const updateUserProfile = ({
  address,
  ...fields
}: Pick<UserProfile, 'firstName' | 'surname' | 'phoneNumber'> & {
  address: APIAddress;
}) => {
  const data: UserProfileUpdateRequestPayload = {
    ...fields,
    deliveriesInfo: [
      {
        address,
        isDefault: true,
      },
    ],
  };

  return API.patch<UserProfile>('/user-profile', data, {
    headers: composeDefaultHeaders(),
  });
};

/**
 * Create payment method
 * @param paymentInfo
 */
export const createPaymentMethod = (paymentInfo: UserPaymentMethod) =>
  // TODO - there is a bug on API that even though we send accept json, API returns string
  // for now, we will assume API returns nothing but status
  API.put<void>(`/user-profile/payment-method/${paymentInfo.id}`, paymentInfo, {
    headers: composeDefaultHeaders(),
  });

/**
 * Deletes saved user's payment method
 * @param id Payment ID to be deleted
 */
export const deletePaymentMethod = (id: UserPaymentMethod['id']) =>
  API.delete<never>(`/user-profile/payment-method/${id}`, {
    headers: composeDefaultHeaders(),
  });

/**
 * Saves an existing payment method as the default one
 * @param id ID of the payment method to be saved as default
 */
export const savePaymentMethodAsDefault = (id: UserPaymentMethod['id']) =>
  API.put<never>(
    `/user-profile/payment-method/${id}`,
    {
      id,
      isDefault: true,
    },
    {
      headers: composeDefaultHeaders(),
    }
  );
